/**
 * Canonical step definitions for the onboarding wizard.
 *
 * Every file that references a step number should import from here
 * instead of using magic numbers. Adding, removing, or reordering
 * steps is a single-array change.
 *
 * DUPE NOTICE: the admin app cannot import from client-app, so it keeps
 * a hand-maintained copy of the step id → key mapping at
 * admin/src/components/OrgOnboardingPanel.tsx (`STEP_KEYS`). The backend
 * can't import from client-app either, and has two of its own
 * hand-maintained single-step constants: app/api/client/onboarding_progress.py
 * (`MATCH_RESULTS_STEP`) and app/scripts/oneoff/backfill_onboarding_completion.py
 * (`MAX_STEP_REACHED_GATE`), both pinned to STEPS.SCAN_RESULTS. If you add,
 * remove, or reorder steps below — or renumber SCAN_RESULTS specifically —
 * mirror the change in all three places.
 */

// ── Types ────────────────────────────────────────────────────

export type Segment =
  | 'intro'
  | 'organisation'
  | 'location'
  | 'upload'
  | 'profile'
  | 'funding'
  | 'scan'
  | 'draft'
export type CardTier = 'standard' | 'video' | 'form' | 'wide-form' | 'progress'

/** Per-step content width: a pixel value or 'full' for unconstrained width. */
export type ContentWidth = number | 'full'

export interface StepDefinition {
  id: number
  key: string
  segment: Segment
  tier: CardTier
  contentWidth: ContentWidth
  isTransition: boolean
  /** If true, step is excluded from the stepper banner segments. */
  excludeFromBanner?: boolean
}

// ── Single source of truth ───────────────────────────────────

export const STEP_DEFINITIONS: StepDefinition[] = [
  {
    id: 1,
    key: 'INTRO_VIDEO',
    segment: 'intro',
    tier: 'video',
    contentWidth: 'full',
    isTransition: false,
  },
  {
    id: 2,
    key: 'INTRO_WELCOME',
    segment: 'intro',
    tier: 'video',
    contentWidth: 'full',
    isTransition: false,
  },
  {
    id: 3,
    key: 'ORG_ENTITY_TYPE',
    segment: 'organisation',
    tier: 'standard',
    contentWidth: 560,
    isTransition: false,
  },
  {
    id: 4,
    key: 'ORG_NUMBER',
    segment: 'organisation',
    tier: 'standard',
    contentWidth: 560,
    isTransition: false,
  },
  {
    id: 5,
    key: 'ORG_DETAILS',
    segment: 'organisation',
    tier: 'form',
    contentWidth: 'full',
    isTransition: false,
  },
  {
    id: 6,
    key: 'ORG_LOCATIONS',
    segment: 'location',
    tier: 'form',
    contentWidth: 'full',
    isTransition: false,
  },
  {
    id: 7,
    key: 'UPLOAD_RESOURCES',
    segment: 'upload',
    tier: 'form',
    contentWidth: 'full',
    isTransition: false,
  },
  {
    id: 8,
    key: 'UPLOAD_WEBPAGES',
    segment: 'upload',
    tier: 'form',
    contentWidth: 'full',
    isTransition: false,
  },
  {
    id: 9,
    key: 'UPLOAD_ANYTHING_ELSE',
    segment: 'upload',
    tier: 'form',
    contentWidth: 720,
    isTransition: false,
  },
  {
    id: 10,
    key: 'PROFILE_GENERATING',
    segment: 'profile',
    tier: 'progress',
    contentWidth: 'full',
    isTransition: true,
  },
  {
    id: 11,
    key: 'PROFILE_REVIEW',
    segment: 'profile',
    tier: 'wide-form',
    contentWidth: 'full',
    isTransition: false,
  },
  {
    id: 12,
    key: 'PROFILE_MILESTONE',
    segment: 'profile',
    tier: 'standard',
    contentWidth: 560,
    isTransition: false,
  },
  {
    id: 13,
    key: 'FUNDING_CREATE',
    segment: 'funding',
    tier: 'standard',
    contentWidth: 560,
    isTransition: false,
  },
  {
    id: 14,
    key: 'FUNDING_GUIDED',
    segment: 'funding',
    tier: 'form',
    contentWidth: 720,
    isTransition: false,
  },
  {
    id: 15,
    key: 'FUNDING_DETAILS',
    segment: 'funding',
    tier: 'form',
    contentWidth: 720,
    isTransition: false,
  },
  {
    id: 16,
    key: 'FUNDING_SUPPORT_DOCS',
    segment: 'funding',
    tier: 'form',
    contentWidth: 'full',
    isTransition: false,
  },
  {
    id: 17,
    key: 'SCAN_RUNNING',
    segment: 'scan',
    tier: 'progress',
    contentWidth: 'full',
    isTransition: true,
  },
  {
    id: 18,
    key: 'SCAN_MILESTONE',
    segment: 'scan',
    tier: 'standard',
    contentWidth: 560,
    isTransition: false,
  },
  {
    id: 19,
    key: 'SCAN_RESULTS',
    segment: 'scan',
    tier: 'wide-form',
    contentWidth: 'full',
    isTransition: false,
  },
  {
    id: 20,
    key: 'DRAFT_GENERATING',
    segment: 'draft',
    tier: 'progress',
    contentWidth: 560,
    isTransition: true,
  },
  {
    id: 21,
    key: 'DRAFT_COMPLETE',
    segment: 'draft',
    tier: 'progress',
    contentWidth: 560,
    isTransition: true,
  },
]

// ── Derived exports ──────────────────────────────────────────

export const STEPS = Object.fromEntries(
  STEP_DEFINITIONS.map((d) => [d.key, d.id]),
) as Record<StepDefinition['key'], number>

export const MAX_STEP = STEP_DEFINITIONS.length

export const TRANSITION_STEPS = new Set(
  STEP_DEFINITIONS.filter((d) => d.isTransition).map((d) => d.id),
)

/** Segment definitions for the stepper banner (excludes pre-banner steps). */
export const SEGMENTS = (() => {
  const bannerSteps = STEP_DEFINITIONS.filter((d) => !d.excludeFromBanner)
  const seen = new Set<Segment>()
  const result: { label: string; startStep: number; endStep: number }[] = []
  for (const def of bannerSteps) {
    if (seen.has(def.segment)) continue
    seen.add(def.segment)
    const stepsInSegment = bannerSteps.filter((d) => d.segment === def.segment)
    result.push({
      label: def.segment.charAt(0).toUpperCase() + def.segment.slice(1),
      startStep: stepsInSegment[0].id,
      endStep: stepsInSegment[stepsInSegment.length - 1].id,
    })
  }
  return result
})()

/** Get the card tier for a step (used by OnboardingShell for layout behaviour). */
export function getCardTier(step: number): CardTier {
  return STEP_DEFINITIONS.find((d) => d.id === step)?.tier ?? 'standard'
}

/** Get the content width for a step (used by OnboardingShell for sizing). */
export function getContentWidth(step: number): ContentWidth {
  return STEP_DEFINITIONS.find((d) => d.id === step)?.contentWidth ?? 560
}
